
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Plus, 
  FileText, 
  Image as ImageIcon, 
  File as FilePdf, 
  Archive as FileArchive,
  File, 
  Download, 
  Trash,
  ExternalLink
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import { LeadAttachment } from "../types";

interface LeadAttachmentsProps {
  leadId: string;
  attachments: LeadAttachment[];
  onUpdate: () => void;
}

export const LeadAttachments = ({ leadId, attachments = [], onUpdate }: LeadAttachmentsProps) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    file_name: "",
    file_url: "",
    file_type: "",
    description: "",
  });
  
  const resetForm = () => {
    setFormData({
      file_name: "",
      file_url: "",
      file_type: "",
      description: "",
    });
  };
  
  const handleSubmit = async () => {
    if (!formData.file_name.trim()) {
      toast.error("File name is required");
      return;
    }
    
    if (!formData.file_url.trim()) {
      toast.error("File URL is required");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Authentication required");
      }
      
      const { error } = await supabase
        .from("lead_attachments")
        .insert({
          lead_id: leadId,
          file_name: formData.file_name,
          file_url: formData.file_url,
          file_type: formData.file_type || null,
          description: formData.description || null,
          created_by: user.id
        });
        
      if (error) throw error;
      
      toast.success("Attachment added successfully");
      setShowForm(false);
      resetForm();
      onUpdate();
      
    } catch (error) {
      console.error("Error saving attachment:", error);
      toast.error("Failed to save attachment");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDelete = async (attachmentId: string) => {
    try {
      const { error } = await supabase
        .from("lead_attachments")
        .delete()
        .eq("id", attachmentId);
        
      if (error) throw error;
      
      toast.success("Attachment deleted successfully");
      onUpdate();
      
    } catch (error) {
      console.error("Error deleting attachment:", error);
      toast.error("Failed to delete attachment");
    }
  };
  
  const getFileIcon = (fileType: string, fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (fileType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) {
      return <ImageIcon className="h-8 w-8 text-blue-500" />;
    }
    
    if (fileType?.includes('pdf') || extension === 'pdf') {
      return <FilePdf className="h-8 w-8 text-red-500" />;
    }
    
    if (['doc', 'docx', 'txt', 'rtf'].includes(extension || '')) {
      return <FileText className="h-8 w-8 text-blue-700" />;
    }
    
    if (['zip', 'rar', 'tar', 'gz'].includes(extension || '')) {
      return <FileArchive className="h-8 w-8 text-amber-600" />;
    }
    
    return <File className="h-8 w-8 text-gray-500" />;
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Attachments</CardTitle>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Attachment
        </Button>
      </CardHeader>
      <CardContent>
        {attachments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No attachments yet. Add your first document for this lead.</p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            {attachments.map((attachment) => (
              <Card key={attachment.id} className="overflow-hidden">
                <div className="p-4 flex gap-4">
                  <div className="flex-shrink-0">
                    {getFileIcon(attachment.file_type || '', attachment.file_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium truncate">{attachment.file_name}</h4>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                        onClick={() => handleDelete(attachment.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                    {attachment.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {attachment.description}
                      </p>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(attachment.created_at), 'MMM d, yyyy')}
                      </div>
                      <div className="flex gap-1">
                        <a 
                          href={attachment.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary-600"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <a 
                          href={attachment.file_url} 
                          download
                          className="text-primary hover:text-primary-600"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
      
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Attachment</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="file_name">File Name *</Label>
              <Input
                id="file_name"
                value={formData.file_name}
                onChange={(e) => setFormData({ ...formData, file_name: e.target.value })}
                placeholder="e.g., Proposal.pdf, Requirements.docx"
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="file_url">File URL *</Label>
              <Input
                id="file_url"
                value={formData.file_url}
                onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                placeholder="https://..."
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="file_type">File Type</Label>
              <Input
                id="file_type"
                value={formData.file_type}
                onChange={(e) => setFormData({ ...formData, file_type: e.target.value })}
                placeholder="e.g., application/pdf, image/png"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the file"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Add Attachment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
